import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  IconButton,
  Input,
  NumberInput,
  NumberInputField,
  Select,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react";
import { MdClose } from "react-icons/md";
import { NODE_TYPES } from "./nodeTypes";

export default function NodePropertiesPanel({ node, onChange, onClose }) {
  if (!node) return null;
  const meta = NODE_TYPES[node.data?.type];
  if (!meta) {
    return (
      <Box p={4}>
        <Text color="red.500">Unknown node type: {node.data?.type}</Text>
      </Box>
    );
  }

  function updateField(name, value, field) {
    const parsed = field?.parse ? field.parse(value) : value;
    onChange({
      ...node,
      data: { ...node.data, [name]: parsed },
    });
  }

  const data = node.data || {};

  return (
    <Box bg="white" borderLeft="1px solid" borderColor="gray.200" h="100%" p={4} overflowY="auto">
      <Flex justify="space-between" align="center" mb={3}>
        <Box>
          <Heading size="sm">{meta.label}</Heading>
          <Text fontSize="xs" color="gray.500" mt={1}>{meta.description}</Text>
        </Box>
        <IconButton size="xs" variant="ghost" icon={<MdClose />} aria-label="Close" onClick={onClose} />
      </Flex>

      {meta.fields.length === 0 ? (
        <Text fontSize="sm" color="gray.500">No configuration for this node type.</Text>
      ) : (
        <VStack align="stretch" spacing={3}>
          {meta.fields.map((field) => {
            const raw = data[field.name];
            const displayValue = field.serialize ? field.serialize(raw) : (raw ?? "");
            const id = `field-${node.id}-${field.name}`;
            return (
              <FormControl key={field.name}>
                <FormLabel htmlFor={id} fontSize="sm" mb={1}>
                  {field.label}
                </FormLabel>
                {field.type === "select" ? (
                  <Select
                    id={id}
                    size="sm"
                    value={displayValue || field.default || ""}
                    onChange={(e) => updateField(field.name, e.target.value, field)}
                  >
                    {field.options.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </Select>
                ) : field.type === "textarea" ? (
                  <Textarea
                    id={id}
                    size="sm"
                    rows={3}
                    placeholder={field.placeholder}
                    value={displayValue}
                    onChange={(e) => updateField(field.name, e.target.value, field)}
                  />
                ) : field.type === "number" ? (
                  <NumberInput
                    id={id}
                    size="sm"
                    value={displayValue === "" ? "" : displayValue}
                    onChange={(_, v) => updateField(field.name, Number.isNaN(v) ? null : v, field)}
                  >
                    <NumberInputField placeholder={field.placeholder} />
                  </NumberInput>
                ) : field.type === "json" ? (
                  <Textarea
                    id={id}
                    size="sm"
                    rows={5}
                    fontFamily="mono"
                    fontSize="xs"
                    placeholder={field.placeholder || '{"key":"value"}'}
                    value={
                      typeof displayValue === "string"
                        ? displayValue
                        : JSON.stringify(displayValue ?? {}, null, 2)
                    }
                    onChange={(e) => {
                      const txt = e.target.value;
                      try {
                        updateField(field.name, txt.trim() ? JSON.parse(txt) : null, field);
                      } catch {
                        // While editing, store raw string; engine ignores
                        // invalid JSON. Pre-save validation should catch this.
                        updateField(field.name, txt, field);
                      }
                    }}
                  />
                ) : (
                  <Input
                    id={id}
                    size="sm"
                    placeholder={field.placeholder}
                    value={displayValue}
                    onChange={(e) => updateField(field.name, e.target.value, field)}
                  />
                )}
              </FormControl>
            );
          })}
        </VStack>
      )}
    </Box>
  );
}
